import classNames from "classnames";
import type { Immutable } from "immer";
import React, { useContext } from "react";
import { useRovingTabList } from "tsi-common-react/src/hooks/useRovingTabList";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { jt, t } from "ttag";

import { Context } from "@reactivated";
import type { templates } from "@reactivated";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/flagship/product_assortment.html`` (FlagshipPage
 * ``ProductAssortmentBlock``, a StaticBlock).
 *
 * The block has no serializable value: the three product tabs (mattresses /
 * bed bases / pillows) are built from ``FlagshipPageSettings`` category trees,
 * filtered by ``purchase_info.price.exists`` (a REQUEST-dependent price gate),
 * plus ``page.retail_location.city`` interpolated into the per-tab note. That
 * request/settings/page-derived data is resolved server-side by the
 * ``product_assortment_block_data`` templatetag and threaded in as side-channel
 * props (BreadcrumbsBlock / CustomerServiceInfoBlock precedent) -- the
 * per-group category lists already have the price filter applied, matching the
 * legacy ``{% if purchase_info.price.exists %}`` gate.
 *
 * Tab switching (roving tabindex, click, and keyboard) is owned here via the
 * shared ``useRovingTabList`` hook and component state, which toggles the
 * ``prodtabs__tab--is-active`` / ``prodtabs__pane--is-active`` modifiers and the
 * ``aria-selected`` / ``tabindex`` attributes. The rendered markup (the
 * vestigial ``data-tabs`` attrs, ``role``/``aria-*``, the ``--is-active``
 * modifiers, the tab/pane ids) is reproduced verbatim so the default render and
 * the post-click states stay byte-identical to the legacy.
 *
 * The static tab-nav images + the offer image are static assets rebuilt from
 * ``STATIC_URL`` (CustomerServiceInfoBlock / StoreDetail precedent), with the
 * legacy eager ``<img>`` (no srcset).
 */

type Group = templates.FlagshipProductAssortmentBlockTemplate["mattresses"];

const TABS = [
    {
        key: "mattresses",
        paneId: "mattresses-pane",
        tabId: "mattresses-tab",
        img: "tabnav-mattresses.jpg",
    },
    {
        key: "bases",
        paneId: "bases-pane",
        tabId: "bases-tab",
        img: "tabnav-bases.jpg",
    },
    {
        key: "pillows",
        paneId: "pillows-pane",
        tabId: "pillows-tab",
        img: "tabnav-pillows.jpg",
    },
] as const;

const ProductGroup = (
    props: Immutable<{ group: Group }>,
): React.JSX.Element => (
    <div className="prodtabs__prodgroup">
        {props.group.categories.map((category, i) => (
            <div className="prodtabs__prodlist" key={i}>
                <h3 className="prodtabs__category">{category.name}</h3>
                <ul>
                    {category.products.map((title, j) => (
                        <li key={j}>{title}</li>
                    ))}
                </ul>
            </div>
        ))}
    </div>
);

const Pane = (
    props: Immutable<{
        paneId: string;
        tabId: string;
        active: boolean;
        city: string;
        group: Group;
        staticURL: string;
    }>,
): React.JSX.Element => {
    const reg = <sup key="reg">&reg;</sup>;
    return (
        <div
            className={classNames("prodtabs__pane", {
                "prodtabs__pane--is-active": props.active,
            })}
            id={props.paneId}
            role="tabpanel"
            aria-labelledby={props.tabId}
        >
            <div className="prodtabs__pane-wrapper l-capped-width">
                <div className="prodtabs__products">
                    <p className="prodtabs__note">
                        {jt`Experience these products at the Tempur-Pedic${reg} ${props.city} Flagship Store.`}
                    </p>
                    <ProductGroup group={props.group} />
                    <p className="prodtabs__note prodtabs__note--border">
                        {t`Due to changing inventory, in-store product selection is not guaranteed. If you'd like to confirm the availability of a specific item, please give us a call and we'll be happy to help.`}
                    </p>
                </div>
                <div className="prodtabs__offer">
                    <img
                        className="responsive-img"
                        src={`${props.staticURL}img/flagship-page/offer.png`}
                        alt={t`Instant $300 gift with the purchase of select mattress sets`}
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            </div>
        </div>
    );
};

export const Template = (
    props: Immutable<templates.FlagshipProductAssortmentBlockTemplate>,
): React.JSX.Element => {
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const staticURL = context?.STATIC_URL ?? "/static/";
    const { activeIndex, getTabProps } = useRovingTabList({
        count: TABS.length,
    });
    const groups = [props.mattresses, props.bed_bases, props.pillows];

    // jt preserves the inline <span> of the legacy ``{% blocktrans %}`` labels.
    const adjustableSpan = <span key="adj">Adjustable </span>;
    const pillowsSpan = <span key="pil"> &amp; more</span>;
    const tabLabels = [
        <>Mattresses</>,
        <>{jt`${adjustableSpan}Bases`}</>,
        <>{jt`Pillows${pillowsSpan}`}</>,
    ];
    const tabTitles = [t`Mattresses`, t`Adjustble Bases`, t`Pillows & More`];

    return (
        <div className="products">
            <div className="lead-in">
                <h1 className="lead-in__headline lead-in__headline--thin lead-in__headline--large">
                    {t`Product Availability`}
                </h1>
                <div className="lead-in__subtext">
                    <p>
                        {richT(
                            t`Tempur-Pedic<sup>®</sup> Stores offer the widest assortment of genuine Tempur-Pedic products.`,
                        )}
                    </p>
                </div>
            </div>

            <div className="prodtabs l-capped-width">
                <span className="ada-screenreader-only">{t`Use arrow keys to navigate tabs`}</span>

                <div
                    className="prodtabs__tabs"
                    role="tablist"
                    aria-label={t`Product Availability`}
                >
                    {TABS.map((tab, i) => (
                        <a
                            href={`#${tab.paneId}`}
                            data-tabs="prodtabs"
                            className={classNames("prodtabs__tab", {
                                "prodtabs__tab--is-active": activeIndex === i,
                            })}
                            title={tabTitles[i]}
                            role="tab"
                            id={tab.tabId}
                            aria-controls={tab.paneId}
                            key={tab.key}
                            {...getTabProps(i)}
                        >
                            <div className="prodtabs__image-wrapper">
                                <img
                                    className="prodtabs__image"
                                    src={`${staticURL}img/flagship-page/${tab.img}`}
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div className="prodtabs__label type_x1point125">
                                {tabLabels[i]}
                            </div>
                        </a>
                    ))}
                </div>

                {TABS.map((tab, i) => (
                    <Pane
                        key={tab.key}
                        paneId={tab.paneId}
                        tabId={tab.tabId}
                        active={activeIndex === i}
                        city={props.city}
                        group={groups[i]}
                        staticURL={staticURL}
                    />
                ))}
            </div>
        </div>
    );
};
